import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { Spin, Tooltip } from "antd";
import { Space, Table } from "antd";
import { dateTimeFormatted, getDatatableWidth } from "utils/helpers";
import { useLanguageTranslator } from "hooks/use-language-translator";
import { caseStatusColors } from "./constants";
import { ConditionalRenderer } from "components";
import styles from "./styles.module.scss";
import { UpdateCaseStatus } from "../update-case-status";
import { EyeIcon, StatusHistoryIcon } from "icons";
import { CaseStatusHistoryModal } from "../cases-cards-layout/case-card/components/case-status-history";
import { UpdateCaseUser } from "./update-case-user";

export const CasesTable = ({
  isLoading,
  cases,
  employees,
  heightOfTable,
  variant,
  handleAddEmailToNote,
  addEmailToNoteLoading,
  caseTotalStatuses,
  refetchCases,
  defaultPageSize = 12,
}) => {
  const actionBtns = [
    {
      key: "viewStatusHistory",
      icon: StatusHistoryIcon,
      clickHandler: handleClickStatusHistory,
    },
  ];
  // const defaultPageSize = 12;
  const { translator } = useLanguageTranslator();
  const columnsTrans = translator("casesTable.tableColumnsLabel");
  const navigate = useNavigate();
  const [caseStatusHistory, setCaseStatusHistory] = useState({
    id: null,
    isOpenModal: false,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  useEffect(() => {
    setCurrentPage(1);
    setPageSize(defaultPageSize);
  }, [window.location.pathname]);

  const finalEmployees = useMemo(() => {
    return employees?.users?.map((item) => {
      return {
        label: item?.username,
        value: item?.email,
      };
    });
  }, [employees?.users]);

  function handlePagination(data) {
    setPageSize(data?.pageSize);
    setCurrentPage(data.current);
  }

  function handleClickStatusHistory(id) {
    setCaseStatusHistory({
      id,
      isOpenModal: true,
    });
  }
  function closeCaseHistoryModal() {
    setCaseStatusHistory({
      ...caseStatusHistory,
      isOpenModal: false,
    });
  }

  let columns = [
    {
      title: columnsTrans.number,
      dataIndex: "key",
      key: "key",
      width: "4%",
      align: "center",

      render: (text, record, index) => {
        const globalIndex = ((currentPage || 1) - 1) * pageSize + index + 1;
        return index < 9 && currentPage === 1 ? `${index + 1}` : globalIndex;
      },
    },
    {
      title: columnsTrans.caseId,
      dataIndex: "caseId",
      key: "caseId",
      width: "8%",
    },
    {
      title: columnsTrans.nameOfPolicyholder,
      dataIndex: "createdByName",
      key: "createdByName",
      width: "9%",
      ellipsis: true,
      align: "center",
      render: (value, record) => {
        return (
          <div>
            <UpdateCaseUser
              employees={finalEmployees}
              refetchCases={refetchCases}
              caseId={record?.caseId}
              value={value}
            />
          </div>
        );
      },
    },
    {
      title: columnsTrans.customerNoSmile,
      dataIndex: "customerNoSmile",
      key: "customerNoSmile",
      width: "8%",
      ellipsis: true,
    },
    {
      title: columnsTrans.invoiceNoSmile,
      dataIndex: "invoiceNoSmile",
      key: "invoiceNoSmile",
      width: "9%",
    },

    {
      title: columnsTrans.creationDate,
      dataIndex: "createdAt",
      key: "createdAt",
      width: "9%",
      render: (value, record) => {
        return dateTimeFormatted(value);
      },
      sorter: (a, b) => {
        const timeA =
          a.createdAt === "None" ? 0 : moment(a.createdAt).valueOf();
        const timeB =
          b.createdAt === "None" ? 0 : moment(b.createdAt).valueOf();
        return timeA - timeB; // oldest → latest, click again for reverse
      },
    },
    {
      title: columnsTrans.lastUpdated,
      dataIndex: "lastUpdated",
      key: "lastUpdated",
      width: "9%",
      render: (value, record) => {
        const date = record?.lastUpdated || record?.createdAt;
        return dateTimeFormatted(date);
      },
      sorter: (a, b) => {
        const dateA = a?.lastUpdated || a?.createdAt;
        const dateB = b?.lastUpdated || b?.createdAt;
        const timeA = dateA === "None" ? 0 : moment(dateA).valueOf();
        const timeB = dateB === "None" ? 0 : moment(dateB).valueOf();
        return timeA - timeB; // oldest → latest, click again for reverse
      },
    },
    {
      title: columnsTrans.percentage,
      dataIndex: "percentage",
      key: "percentage",
      width: "7%",
      align: "center",
      render: (_, record) => {
        return <div>{record?.percentage}%</div>;
      },
    },
    {
      title: columnsTrans.status,
      dataIndex: "status",
      key: "status",
      width: variant === "addNOte" ? "9%" : "10%",
      ellipsis: true,
      align: "center",
      render: (value, record) => {
        return (
          <UpdateCaseStatus
            caseStatus={record?.status}
            caseId={record?.caseId}
            caseTotalStatuses={caseTotalStatuses}
            refetchCases={refetchCases}
          />
        );
      },
    },

    {
      title: columnsTrans.action,
      key: "action",
      width: "7%",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <ConditionalRenderer condition={variant !== "addNote"}>
            <div style={{ display: "flex", gap: "5px" }}>
              {actionBtns.map((actionBtn) => (
                <Tooltip
                  title={translator(`casesTable.${actionBtn?.key}`)}
                  key={actionBtn?.key}
                >
                  <div
                    className={styles.viewDetailsBtn}
                    onClick={() => actionBtn.clickHandler(record?.caseId)}
                    key={actionBtn.key}
                  >
                    {<actionBtn.icon />}
                  </div>
                </Tooltip>
              ))}
              <button
                className={styles.viewDetailsBtn}
                onClick={() =>
                  navigate(`/cases/${record?.caseId}`, {
                    state: {
                      origin: "detail",
                      employees: finalEmployees,
                    },
                  })
                }
              >
                <img
                  src="./assets/icons/eye.svg"
                  alt="Icon"
                  height={15}
                  width={15}
                />
              </button>

              {/* <button
              className={styles.viewDetailsBtn}
              onClick={() =>
                navigate(`/cases/${record?.caseId}`, {
                  state: {
                    origin: "upload",
                    employees: finalEmployees,
                  },
                })
              }
            >
              <UploadFilesRed style={{ height: "13px", width: "13px" }} />
            </button>
            <button
              className={styles.viewDetailsBtn}
              onClick={() =>
                navigate(`/cases/${record?.caseId}`, {
                  state: {
                    origin: "notes",
                    finalEmployees: employees,
                  },
                })
              }
            >
              <NotesIcon style={{ height: "13px", width: "13px" }} />
            </button> */}
            </div>
          </ConditionalRenderer>
          <ConditionalRenderer condition={variant === "addNote"}>
            <Tooltip title={record?.linked ? columnsTrans.removeNote : ""}>
              <div
                className={`${styles.addNote} ${record.linked ? styles.linked : ""}`}
                onClick={() =>
                  handleAddEmailToNote({
                    case_id: record?.caseId,
                    linked: record?.linked,
                    type: record.linked ? "remove" : "add",
                  })
                }
              >
                {record.linked ? columnsTrans.assigned : columnsTrans.addToNote}
              </div>
            </Tooltip>
          </ConditionalRenderer>
        </Space>
      ),
    },
  ];

  columns =
    variant === "addNote"
      ? columns.filter((item) => item.dataIndex !== "status")
      : columns;

  return (
    <div
      className={`${variant === "addNote" ? styles.addNoteTableContainer : styles.tableContainer}`}
    >
      <Spin spinning={isLoading || !!addEmailToNoteLoading}>
        <Table
          onChange={handlePagination}
          columns={columns}
          dataSource={cases}
          rowClassName={styles.row}
          scroll={{
            y: `calc(${heightOfTable} - 100px)`,
            x: getDatatableWidth(),
          }} // Set the width for horizontal scrolling
          style={{ width: "100%", height: heightOfTable }}
          className={styles.userListTable}
          sticky={true}
          pagination={{
            position: ["bottomCenter"],
            pageSize: pageSize,
            current: currentPage,
          }}
        />
      </Spin>
      <CaseStatusHistoryModal
        caseStatusHistory={caseStatusHistory}
        closeCaseHistoryModal={closeCaseHistoryModal}
      />
    </div>
  );
};
